import { useState } from "react";
import { useSearchParams } from "react-router";
import { useForm } from "@tanstack/react-form";
import { Button, Icon } from "@sellar-io/components";
import { useDeliveryPreferencesStore } from "../deliveryPreferencesStore";
import { buyerDetailsSchema } from "../stepRegistry";
import { OrderBanner } from "../components/OrderBanner";
import { OrderSummaryDrawer } from "../components/OrderSummaryDrawer";
import { useOrderLinkFlowOrder } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";
import { useAuthCreateOrderLinkSignupCode } from "../../../../../hooks/api/v2/auth/useAuthCreateOrderLinkSignupCode";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";

const inputClass =
  "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-800 outline-none focus:border-zinc-400";

const GreenTick = () => (
  <svg
    className="h-5 w-5 text-emerald-500"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.707 7.293a1 1 0 00-1.414 0L10 14.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l6-6a1 1 0 000-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

export default function BuyerDetailsStep() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sameAsContact, setSameAsContact] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: order } = useOrderLinkFlowOrder(token);
  const store = useDeliveryPreferencesStore();
  const contactDefaults = store.deliveryContact;
  const authSignupCodeMutation = useAuthCreateOrderLinkSignupCode();

  const form = useForm({
    defaultValues: {
      email: store.buyerDetails?.email ?? "",
      name: store.buyerDetails?.name ?? "",
      phone: store.buyerDetails?.phone ?? "",
    },
    validators: {
      onSubmit: buyerDetailsSchema,
    },
    onSubmit: ({ value }) => {
      setSubmitError(null);
      store.setBuyerDetails(value);
      authSignupCodeMutation.mutate(
        { data: { email: value.email }, orderFlowToken: token! },
        {
          onSuccess: () => store.next(),
          onError: (error) => {
            setSubmitError(
              apiV2AssertError(error)?.response?.data.message ??
                "Failed to send verification code. Please try again.",
            );
          },
        },
      );
    },
  });

  const handleSameAsContactToggle = (checked: boolean) => {
    setSameAsContact(checked);
    if (checked && contactDefaults) {
      form.setFieldValue("email", contactDefaults.email);
      form.setFieldValue("name", contactDefaults.name);
      form.setFieldValue("phone", contactDefaults.phone);
    } else if (!checked) {
      form.setFieldValue("email", "");
      form.setFieldValue("name", "");
      form.setFieldValue("phone", "");
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      {order && (
        <OrderBanner order={order} onShowDetails={() => setDrawerOpen(true)} />
      )}

      <div className="w-full flex-1 border-t border-zinc-200 bg-neutral-100 px-5 py-8">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-5">
            <button
              type="button"
              onClick={() => store.prev()}
              className="mb-3 flex cursor-pointer items-center gap-2 text-zinc-800 hover:text-zinc-600"
            >
              <Icon icon="CHEVRON_LEFT" size={0.9} />
              <h2 className="text-xl font-semibold">Your details</h2>
            </button>
            <p className="mt-1 text-sm text-zinc-500">
              Please provide your details if they're different to your delivery
              contact
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3">
              <input
                type="checkbox"
                checked={sameAsContact}
                onChange={(e) => handleSameAsContactToggle(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 accent-zinc-800"
              />
              <span className="text-sm text-zinc-800">
                My delivery contact details are the same as my details
              </span>
            </label>

            <form.Field name="email">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Email address
                  </label>
                  <div className="relative">
                    <input
                      className={inputClass + (sameAsContact ? " pr-10" : "")}
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {sameAsContact && field.state.value && (
                      <span className="absolute top-1/2 right-3 -translate-y-1/2">
                        <GreenTick />
                      </span>
                    )}
                  </div>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="name">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Contact name
                  </label>
                  <div className="relative">
                    <input
                      className={inputClass + (sameAsContact ? " pr-10" : "")}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {sameAsContact && field.state.value && (
                      <span className="absolute top-1/2 right-3 -translate-y-1/2">
                        <GreenTick />
                      </span>
                    )}
                  </div>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="phone">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-zinc-800">
                    Phone number
                  </label>
                  <div className="relative">
                    <input
                      className={inputClass + (sameAsContact ? " pr-10" : "")}
                      type="tel"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {sameAsContact && field.state.value && (
                      <span className="absolute top-1/2 right-3 -translate-y-1/2">
                        <GreenTick />
                      </span>
                    )}
                  </div>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {submitError && (
              <p className="text-sm text-red-500">{submitError}</p>
            )}
            <Button
              type="submit"
              disabled={authSignupCodeMutation.isPending}
              className="mt-1 w-full rounded-xl bg-zinc-900 py-3.5 text-sm font-semibold tracking-wide text-white uppercase transition-colors hover:bg-zinc-800 disabled:opacity-60"
            >
              {authSignupCodeMutation.isPending ? "Sending code…" : "Continue"}
            </Button>
            <p className="text-center text-xs text-zinc-500">
              You need to continue to save your details
            </p>
          </form>
        </div>
      </div>

      {order && (
        <OrderSummaryDrawer
          active={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          order={order}
        />
      )}
    </div>
  );
}
